import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentAppState, updateAppState } from './AlmeAppState';
import { AlmeApiUtils } from '../../AlmeApiUtils';
import { createLiveChatNotificationApi, createAlmeApi, createApiSetting } from '../../AlmeApi';
import { updateAppConfigState } from '../../alme-config/AlmeAppConfig';
import AlmeHeader from '../AlmeHeader/AlmeHeader';
import AlmeHistory from '../AlmeHistory/AlmeHistory';
import AlmeFooter from '../AlmeFooter/AlmeFooter';
import { renderLaunchPointIcon } from '../AlmeLaunchPoint/AlmeLaunchPoint';
import { AlmeMinimizeUtils } from '../AlmeHeader/AlmeMinimize/AlmeMinimize';
import { AlmeCloseUtils } from '../AlmeHeader/AlmeClose/AlmeClose';
import AlmeLoader from '../AlmeLoader/AlmeLoader';
import { AppContainer, ContentContainer } from './AppContainer';
import Grow from '../AlmeUtilities/Transitions/Grow';
import { renderSessionTimeoutDialog } from '../AlmeSession/IvaSessionTimeout';
import { SessiontTimeoutUtils } from '../AlmeSession/AlmeSessionTimeoutUtils';
import { processLiveChatStartAction } from '../ResponseActions/AlmeLiveChatStart/AlmeLiveChatStartUtils';
import { processLiveChatEndAction } from '../ResponseActions/AlmeLiveChatEnd/AlmeLiveChatEndUtils';
import { processSwitchAccountConfirmationAction } from '../ResponseActions/SwitchAccountConfirmation/SwitchAccountConfirmation';
import { processReplayInputAction } from '../ResponseActions/ReplayInput/ReplayInput';
import AlmeLiveChatControls from '../LiveChat/AlmeLiveChatControls/AlmeLiveChatControls';
import { OnSendTypingEvent } from '../AlmeInput/MessageInputBox';
import { useLiveChatConnected } from '../../hooks/useLiveChatConnected';
import { usePollForChat } from '../../hooks/usePollForChat';
import { OnContinueChat, OnEndChat } from '../LiveChat/AlmeLiveChatReportingEvents';
import { getLogger } from '../../utils/DebugUtils';
import initializeAppEvents from './initializeAppEvents';
import initializeAlmeApp from './initializeAlmeApp';
import { MarkUsedResponse } from '../LiveChat/AlmeLiveChatSurvey/AlmeLiveChatSurveyUtils';
import { ObjectDiff } from '../../utils/ObjectUtils';
import { callIfFunction } from '../../utils/FunctionUtils';
import { usePollForHistory } from '../../hooks/usePollForHistory';
import { usePostMessageListener } from '../../hooks/usePostMessageListener';
import { useSetChatStatus } from '../../hooks/useSetChatStatus';
import { useIVAChatStatus, useIVASetChatStatus } from '../../IVAChatStatusProvider';
import { isInlineFormResponse } from '../AlmeInlineForm/InlineFormResponseAction';
import { focusChatIcon } from '../AlmeUtilities/dom-utils';
import TabTrap from 'syf-component-library/helpers/TabTrap';
import { processPointOfNeedHook } from './initialize/processPointOfNeedHook';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { isPeaks, getCurrentAccount } from "./../../platform/identifiers";
import { processSwitchTenantConfirmationAction } from '../ResponseActions/SwitchTenantConfirmation/SwitchTenantConfirmation';
const processLastResponse = (almeState, setAlmeState, almeApiUtils, appConfig, isAccountSwitched, props, lastResponse) => {
    if (lastResponse) {
        // Process last history item for LiveChatStart response action
        processLiveChatStartAction(lastResponse, almeState, setAlmeState);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        processLiveChatEndAction(lastResponse, almeState, setAlmeState, appConfig, almeApiUtils);
        processSwitchAccountConfirmationAction(lastResponse, almeApiUtils, almeState, setAlmeState, isAccountSwitched, props);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        processReplayInputAction(lastResponse, almeState, almeApiUtils);
        processSwitchTenantConfirmationAction(lastResponse, almeApiUtils, almeState, setAlmeState);
    }
};
const getAppTabIndex = (isDialogOpen) => isDialogOpen ? -1 : 0;
const hasLivechatSurveyLoaded = (history) => history.some(h => { var _a; return h.fromHistory && ((_a = h.extensionData) === null || _a === void 0 ? void 0 : _a.surveyUrl); });
const canShowApp = (almeState) => !!(almeState.historyLoaded && almeState.isOpen);
function updateInteractionId(appConfig, almeState, setAlmeAppState, almeApiUtils) {
    if ((appConfig.main.platform === 'VISTA' && appConfig.main.interactionId) && (!almeState.interactionId || almeState.interactionId !== appConfig.main.interactionId)) {
        const userMetadata = appConfig.main.userMetadata;
        almeApiUtils.createNewSessionData();
        setAlmeAppState(Object.assign(Object.assign({}, almeState), { interactionId: appConfig.main.interactionId, currentEnterpriseId: userMetadata.currentEnterpriseId }));
    }
}
function updateCurrentEnterpriseIdForPeaks(appConfig, almeState) {
    const userMetadata = appConfig.main.userMetadata;
    almeState.currentEnterpriseId = userMetadata.currentEnterpriseId;
    updateAppState(almeState);
}
function updateStateFromProp(props, state, setAlmeAppState, isInitialized) {
    if (typeof props.isOpen === "boolean"
        && props.isOpen !== state.isOpen
        && isInitialized) { //to avoid updating state multiple times
        setAlmeAppState(Object.assign(Object.assign({}, state), { isOpen: props.isOpen }));
    }
}
function initialAlmeState(propsIsOpen, appConfig) {
    const currentAppState = getCurrentAppState();
    const userMetadata = appConfig.main.userMetadata;
    const currentEnterpriseId = appConfig.main.platform === 'PEAKS' && currentAppState.currentEnterpriseId === undefined ? userMetadata === null || userMetadata === void 0 ? void 0 : userMetadata.currentEnterpriseId : currentAppState.currentEnterpriseId;
    return ({
        history: [],
        historyLoaded: false,
        isOpen: currentAppState.isOpen || propsIsOpen,
        isLoggedIn: currentAppState.isLoggedIn,
        clientSessionId: currentAppState.clientSessionId,
        hasPreviousConversation: false,
        accessKey: currentAppState.accessKey || appConfig.main.appAccessKey,
        sessionMessages: currentAppState.sessionMessages,
        customerCheckStatusMessage: currentAppState.customerCheckStatusMessage,
        sessionExpired: currentAppState.sessionExpired,
        lastActivityTime: currentAppState.lastActivityTime,
        isLiveChatConnected: false,
        isLiveChatSessionTimeout: false,
        isInitialAppEventsSent: false,
        interactionId: currentAppState.interactionId,
        currentEnterpriseId: currentEnterpriseId,
        wasSpanishLiveChatLinkClicked: false,
        isSpanishLiveChatConnected: false,
    });
}
const AlmeApp = (props) => {
    var _a;
    const { chatPosition, launchPointName, ponEnterpriseId } = useIVAChatStatus();
    const setChatStatus = useIVASetChatStatus();
    const [almeState, setAlmeState] = useState(() => initialAlmeState(props.isOpen, props.appConfig));
    const history = almeState.history;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [appConfig, setAppConfig] = useState(props.appConfig);
    const isInitialized = useRef(false);
    /**
     *  `isAppEventsSent` is same as `isInitialAppEventsSent` AlmeAppState
     *   using this ref variable as well to avoid sending AppEvents multiple times
     **/
    const isAppEventsSent = useRef(false);
    const isAccountSwitched = useRef(false);
    const logger = getLogger(appConfig.UiLogLevel);
    const setAlmeStateWrapper = useCallback((state) => {
        const changes = ObjectDiff(almeState, state);
        if (Object.keys(changes).length) {
            logger.infoTrace("State change", changes, "Original State", almeState, "New State", state);
            updateAppState(state);
            setAlmeState(state);
        }
    }, [almeState, setAlmeState, logger]);
    /**
     * State update is required in case prop changes, occurs mainly in node module implementation
     */
    updateAppConfigState(props.appConfig, appConfig, setAppConfig);
    updateStateFromProp(props, almeState, setAlmeStateWrapper, isInitialized.current);
    const apiSetting = createApiSetting(appConfig.main.apiHostName);
    const almeApi = createAlmeApi(apiSetting, logger);
    const liveChatNotificationApi = createLiveChatNotificationApi(apiSetting, logger);
    const almeApiUtils = new AlmeApiUtils(almeState, setAlmeStateWrapper, appConfig, almeApi, liveChatNotificationApi);
    const markUsedResponse = MarkUsedResponse(almeState, setAlmeStateWrapper);
    useLiveChatConnected(almeState.isLiveChatConnected, appConfig, setAppConfig);
    const submitAlmeInput = almeApiUtils.submitAlmeInput.bind(almeApiUtils);
    const onDisplayLinkClick = almeApiUtils.onDisplayLinkClick.bind(almeApiUtils);
    const clearSessionData = almeApiUtils.clearSessionData.bind(almeApiUtils);
    const almeMinimizeUtils = new AlmeMinimizeUtils(almeState, setAlmeStateWrapper, appConfig);
    setChatStatus.setInternalMinimizeChatHook(() => {
        almeMinimizeUtils.onMinimizeClick()
            .then(() => {
            callIfFunction(props.onMinimize);
            focusChatIcon();
        })
            .catch((error) => logger.error(error));
    });
    setChatStatus.initiatePointOfNeedChatHook(() => {
        if (!almeState.isLiveChatConnected) {
            const ponProps = { setChatStatus: setChatStatus, launchPointName: launchPointName, ponEnterpriseId: ponEnterpriseId };
            processPointOfNeedHook(almeState, setAlmeStateWrapper, appConfig, almeApiUtils, isAppEventsSent, logger, ponProps);
        }
        else {
            console.log('User is active with livechat agent');
        }
    });
    const onMinimizeClick = setChatStatus.minimizeChat;
    const almeCloseUtils = new AlmeCloseUtils(almeState, setAlmeStateWrapper, appConfig, almeApiUtils, clearSessionData);
    function onCloseClick() {
        almeCloseUtils.onCloseClick()
            .then(() => {
            setIsDialogOpen(false);
            setChatStatus.closeChat();
            isAppEventsSent.current = false;
            callIfFunction(props.onClose);
            focusChatIcon();
        })
            .catch((error) => logger.error(error));
    }
    const submitFormAppEvent = almeApiUtils.submitFormAppEvent.bind(almeApiUtils);
    const sessionTimeoutUtils = new SessiontTimeoutUtils(almeState, setAlmeStateWrapper, appConfig, almeApiUtils);
    function onSessionTimeout() {
        sessionTimeoutUtils.onSessionTimeout()
            .catch((error) => logger.error(error));
    }
    const onUserSessionRefresh = sessionTimeoutUtils.onUserSessionRefresh.bind(sessionTimeoutUtils);
    usePostMessageListener(almeState, setAlmeStateWrapper, appConfig, almeApi, liveChatNotificationApi, setChatStatus, setAppConfig);
    function isSelectedAccountUpdated(appConfiguration) {
        let currentEnterpriseId = undefined;
        currentEnterpriseId = getCurrentAccount();
        const userMetaData = appConfiguration.main.userMetadata;
        return (currentEnterpriseId === userMetaData.currentEnterpriseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        /**
         * Alme app initialization sequence.
         * 1. Send a GetConfiguration Alme request convert from Key/value pair to property/value and save the results in the almeState.config.
         * 2. Send a GetHistory Alme request and if history exists:
         *    - Disable all auto navigation by setting response.navUrl.AutoNavigationType = 'None'.
         *    - Save the history in the almeState.history.
         * 3. Else if no history exists Send a "WelcomeUser" AppEvent request and save the response in the almeState.history.
         */
        updateInteractionId(appConfig, almeState, setAlmeStateWrapper, almeApiUtils);
        if (isInitialized.current) {
            if (!isAppEventsSent.current) {
                if (isPeaks() && almeState.isOpen && !hasHistory && !isSelectedAccountUpdated(appConfig)) {
                    const currentEnterpriseId = getCurrentAccount();
                    const userMetaData = appConfig.main.userMetadata;
                    userMetaData.currentEnterpriseId = (currentEnterpriseId === undefined ? userMetaData === null || userMetaData === void 0 ? void 0 : userMetaData.currentEnterpriseId : currentEnterpriseId);
                    updateAppConfigState(appConfig, props.appConfig, setAppConfig);
                    updateCurrentEnterpriseIdForPeaks(appConfig, almeState);
                }
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                initializeAppEvents(almeState, setAlmeStateWrapper, appConfig, isAppEventsSent, almeApiUtils);
            }
        }
        else {
            isInitialized.current = true;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            initializeAlmeApp(almeState, setAlmeStateWrapper, appConfig, setAppConfig, almeApiUtils, props.isOpen);
        }
    });
    const onEndChat = OnEndChat(almeApiUtils, almeState, setAlmeStateWrapper);
    const onSendTypingEvent = OnSendTypingEvent(almeApiUtils);
    const onContinueChat = OnContinueChat(almeApiUtils);
    usePollForChat(appConfig, isInitialized, almeState, almeApiUtils);
    usePollForHistory(almeState, setAlmeStateWrapper, appConfig, almeApiUtils, history);
    const hasHistory = history.length > 0;
    const tabIndex = getAppTabIndex(isDialogOpen);
    const lastResponse = history[history.length - 1];
    const isLastResponseInlineForm = isInlineFormResponse(lastResponse);
    const isLivechatSurveyLoaded = hasLivechatSurveyLoaded(history);
    useSetChatStatus(almeState.historyLoaded, hasHistory, almeState.isOpen, (_a = window.NITGlobal) === null || _a === void 0 ? void 0 : _a.clientAvailable);
    processLastResponse(almeState, setAlmeStateWrapper, almeApiUtils, appConfig, isAccountSwitched, props, lastResponse);
    return (React.createElement(ErrorBoundary, { appConfig: appConfig },
        React.createElement(React.Fragment, null,
            React.createElement(TabTrap, null, React.createElement(Grow, { style: { transformOrigin: '100% 100% 0' }, show: canShowApp(almeState), duration: 100 },
                React.createElement(AppContainer, { "data-private": true, id: 'nit-alme-window-root', "data-testid": 'nit-alme-window-root', "aria-label": 'IVA Chat Container', role: "dialog", position: chatPosition, mobileBreakpoint: appConfig.mobileBreakpoint, zIndex: appConfig.zIndex },
                    React.createElement(ContentContainer, null,
                        React.createElement(AlmeHeader, { onMinimizeClick: onMinimizeClick, onCloseClick: onCloseClick, almeAppConfig: appConfig, setIsDialogOpen: setIsDialogOpen, focusProps: { disabled: isDialogOpen && !almeState.sessionExpired, tabIndex: almeState.sessionExpired ? 0 : tabIndex }, isLiveChatConnected: almeState.isLiveChatConnected, isLoggedIn: almeState.isLoggedIn, chatId: almeState.chatId, isLivechatSurveyLoaded: isLivechatSurveyLoaded, isSessionExpired: almeState.sessionExpired, almeApiUtils: almeApiUtils, isAccountSwitched: isAccountSwitched, currentEnterpriseId: almeState.currentEnterpriseId }),
                        React.createElement(AlmeHistory, { history: history, appConfig: appConfig, focusProps: { tabIndex }, isLiveChatConnected: almeState.isLiveChatConnected, setIsDialogOpen: setIsDialogOpen, isDialogOpen: isDialogOpen, onDisplayLinkClick: onDisplayLinkClick, onSubmitFormAppEvent: submitFormAppEvent, onContinueChat: onContinueChat, onEndChat: onEndChat, markUsedResponse: markUsedResponse, almeState: almeState, setAlmeAppState: setAlmeStateWrapper }),
                        almeState.isLiveChatConnected ?
                            React.createElement(AlmeLiveChatControls, { response: lastResponse, appConfig: appConfig, isLiveChatConnected: almeState.isLiveChatConnected, focusProps: { disabled: isDialogOpen }, setIsDialogOpen: setIsDialogOpen, onEndChat: onEndChat, isDialogOpen: isDialogOpen, markUsedResponse: markUsedResponse }) : null,
                        React.createElement(AlmeFooter
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        , { 
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            submitFunc: submitAlmeInput, almeAppConfig: appConfig, isLiveChatConnected: almeState.isLiveChatConnected, 
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises
                            onSendTypingEvent: onSendTypingEvent, focusProps: { disabled: almeState.sessionExpired || isDialogOpen || isLastResponseInlineForm || !almeState.isInitialAppEventsSent } }),
                        React.createElement(AlmeLoader, { loaded: almeState.isInitialAppEventsSent }),
                        // eslint-disable-next-line @typescript-eslint/no-misused-promises
                        renderSessionTimeoutDialog(almeState, appConfig, isLivechatSurveyLoaded, onUserSessionRefresh, onSessionTimeout, onCloseClick))))),
            renderLaunchPointIcon(appConfig, almeState.historyLoaded, hasHistory, !!almeState.isOpen))));
};
export default AlmeApp;
